import React from 'react';
import type { LscLeague } from '../types';
import { leagueInitials, leagueLogoAltText } from '../leagueBranding';

export type LeagueLogoSize = 'sm' | 'md' | 'badge' | 'preview';

interface LeagueLogoProps {
  league: Pick<LscLeague, 'name' | 'logo_url'>;
  size?: LeagueLogoSize;
  className?: string;
  testId?: string;
}

export const LEAGUE_LOGO_SIZE_CLASSES: Record<
  LeagueLogoSize,
  { container: string; image: string; text: string }
> = {
  sm: {
    container: 'h-8 w-8',
    image: 'max-h-8 max-w-8',
    text: 'text-xs',
  },
  md: {
    container: 'h-10 w-10',
    image: 'max-h-10 max-w-10',
    text: 'text-sm',
  },
  badge: {
    container: 'h-11 w-11',
    image: 'max-h-10 max-w-10',
    text: 'text-sm',
  },
  preview: {
    container: 'h-[100px] w-40',
    image: 'max-h-[84px] max-w-[144px]',
    text: 'text-base',
  },
};

const LeagueLogo: React.FC<LeagueLogoProps> = ({
  league,
  size = 'md',
  className = '',
  testId = 'league-logo',
}) => {
  const sizeClasses = LEAGUE_LOGO_SIZE_CLASSES[size];
  const initials = leagueInitials(league.name);
  const containerClass = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-secondary-700 bg-secondary-900 ${sizeClasses.container} ${className}`;

  if (league.logo_url) {
    return (
      <span className={containerClass} data-testid={testId}>
        <img
          src={league.logo_url}
          alt={leagueLogoAltText(league)}
          className={`h-full w-full object-contain ${sizeClasses.image}`}
        />
      </span>
    );
  }

  return (
    <span
      className={`${containerClass} bg-secondary-800 font-semibold text-primary-200 ${sizeClasses.text}`}
      aria-label={`${league.name} initials`}
      data-testid={`${testId}-fallback`}
    >
      {initials}
    </span>
  );
};

export default LeagueLogo;
